import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  CardHeader,
  CardContent,
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  HelpText,
  AutomationServicesHeader,
  AutomationServiceCard,
  AutomationServiceCardHeader
} from './styles';
import {
  CheckBox,
  Input,
  Select,
  RadioButtonGroup,
  Typography,
  Button
} from '../../../../../../components';
import configurationService from '../../../../../../api/configurationService';
import { useUi } from '../../../../../../context/ui.context';
import { icons } from '../../../../../../icons';

interface AutoOrderService {
  product: number | null;
  product_cycle: number | null;
  product_params: string;
}

interface BillingFormData {
  require_credit_card: boolean;
  signup_charge_amount: number;
  signup_charge_amount_action: string;
  credit_required: number;
  credit_limit: number;
  minim_uptodate_credit_for_invoice_payment: number;
  credit_required_with_agreement: number;
  credit_limit_with_agreement: number;
  billing_cycle_as_calendar_month: boolean;
  auto_settle_invoiced_periods_external_billing: boolean;
  company_info: string;
  sender_name: string;
  sender_email: string;
  use_custom_logo_for_pdf_invoices: boolean;
  pdf_logo_file_name: string;
  fraud_check: boolean;
  maxmind_fraud_score: number;
  enable_maxmind_insights: boolean;
  skip_fraud_check_for_active_clients: boolean;
  automatically_mark_new_orders_as_fraud_for_clients_with_fraud_order: boolean;
  auto_create_order: boolean;
  auto_order_services: AutoOrderService[];
  client_initial_credit: number;
  generate_invoices: boolean;
  generate_invoices_after: boolean;
  generate_invoices_after_dt: string;
  generate_invoices_after_time: string;
  do_not_invoice_services_with_zero_price: boolean;
  auto_merge_service_cycle_invoices: boolean;
  send_notifications_for_unpaid_invoices: boolean;
  create_todo_on_invoice_payment: boolean;
  auto_settle_usage: boolean;
  auto_pay_invoice_only_when_enough_credit: boolean;
  add_credit_min_value: number;
  invoice_notifications_enabled: boolean;
  first_invoice_payment_reminder_remaining_hours: number;
  first_invoice_payment_reminder_notification_template: string;
  second_invoice_payment_reminder_remaining_hours: number;
  second_invoice_payment_reminder_notification_template: string;
  first_invoice_payment_overdue_hours: number;
  first_invoice_payment_overdue_notification_template: string;
  second_invoice_payment_overdue_hours: number;
  second_invoice_payment_overdue_notification_template: string;
  third_invoice_payment_overdue_hours: number;
  third_invoice_payment_overdue_notification_template: string;
  invoice_payment_confirmation_notification_template: string;
  invoice_refund_confirmation_notification_template: string;
  service_notifications_enabled: boolean;
  service_suspended_notification_template: string;
  service_resumed_notification_template: string;
  auto_suspend: boolean;
  auto_suspend_delay_hours_enabled: boolean;
  auto_suspend_delay_hours: number;
  auto_suspend_delay_credit_enabled: boolean;
  auto_suspend_delay_credit: number;
  auto_suspend_notification_template: string;
  suspend_instead_of_terminate: boolean;
  auto_terminate: boolean;
  auto_terminate_delay_hours: number;
  auto_terminate_notification_template: string;
  auto_recreate_terminated_service: boolean;
  auto_terminate_buckets: boolean;
  auto_terminate_buckets_delay_hours: number;
  auto_terminate_gpu: boolean;
  auto_terminate_gpu_delay_hours: number;
  auto_terminate_bare_metal: boolean;
  auto_terminate_bare_metal_delay_hours: number;
}

const getInputValue = (value: any) => value?.target ? value.target.value : value;

const normalizeStringValue = (value: any): string => {
  const inputValue = getInputValue(value);

  if (inputValue === null || inputValue === undefined) {
    return '';
  }

  if (typeof inputValue === 'string') {
    return inputValue;
  }

  if (typeof inputValue === 'number' || typeof inputValue === 'boolean') {
    return String(inputValue);
  }

  if (typeof inputValue === 'object') {
    const objectValue = inputValue as Record<string, any>;
    const stringKeys = ['value', 'default', 'name', 'email', 'title', 'label', 'display_name'];

    for (const key of stringKeys) {
      if (key in objectValue) {
        return normalizeStringValue(objectValue[key]);
      }
    }

    return '';
  }

  return String(inputValue);
};

const normalizeBillingConfiguration = (data: any) => ({
  ...data,
  company_info: normalizeStringValue(data.company_info),
  sender_name: normalizeStringValue(data.sender_name),
  sender_email: normalizeStringValue(data.sender_email),
  pdf_logo_file_name: normalizeStringValue(data.pdf_logo_file_name),
  auto_order_services: Array.isArray(data.auto_order_services)
    ? data.auto_order_services.map((service: any) => ({
        product: service.product ?? null,
        product_cycle: service.product_cycle ?? null,
        product_params: normalizeStringValue(service.product_params),
      }))
    : [],
});

const BillingPage: React.FC = () => {
  const [formData, setFormData] = useState<BillingFormData>({
    require_credit_card: false,
    signup_charge_amount: 0,
    signup_charge_amount_action: 'add_to_client',
    credit_required: 0,
    credit_limit: 0,
    minim_uptodate_credit_for_invoice_payment: 0,
    credit_required_with_agreement: 0,
    credit_limit_with_agreement: 0,
    billing_cycle_as_calendar_month: false,
    auto_settle_invoiced_periods_external_billing: false,
    company_info: '',
    sender_name: '',
    sender_email: '',
    use_custom_logo_for_pdf_invoices: false,
    pdf_logo_file_name: '',
    fraud_check: false,
    maxmind_fraud_score: 90,
    enable_maxmind_insights: false,
    skip_fraud_check_for_active_clients: false,
    automatically_mark_new_orders_as_fraud_for_clients_with_fraud_order: false,
    auto_create_order: false,
    auto_order_services: [],
    client_initial_credit: 0,
    generate_invoices: false,
    generate_invoices_after: false,
    generate_invoices_after_dt: '',
    generate_invoices_after_time: '',
    do_not_invoice_services_with_zero_price: false,
    auto_merge_service_cycle_invoices: false,
    send_notifications_for_unpaid_invoices: false,
    create_todo_on_invoice_payment: false,
    auto_settle_usage: false,
    auto_pay_invoice_only_when_enough_credit: false,
    add_credit_min_value: 0.01,
    invoice_notifications_enabled: false,
    first_invoice_payment_reminder_remaining_hours: 168,
    first_invoice_payment_reminder_notification_template: 'billing.invoice.payment.reminder',
    second_invoice_payment_reminder_remaining_hours: 48,
    second_invoice_payment_reminder_notification_template: 'billing.invoice.payment.reminder',
    first_invoice_payment_overdue_hours: 24,
    first_invoice_payment_overdue_notification_template: 'billing.invoice.payment.overdue',
    second_invoice_payment_overdue_hours: 72,
    second_invoice_payment_overdue_notification_template: 'billing.invoice.payment.overdue',
    third_invoice_payment_overdue_hours: 120,
    third_invoice_payment_overdue_notification_template: 'billing.invoice.payment.overdue',
    invoice_payment_confirmation_notification_template: 'billing.invoice.payment.confirmation',
    invoice_refund_confirmation_notification_template: 'billing.invoice.refund.confirmation',
    service_notifications_enabled: false,
    service_suspended_notification_template: 'service.suspended',
    service_resumed_notification_template: 'service.resumed',
    auto_suspend: false,
    auto_suspend_delay_hours_enabled: false,
    auto_suspend_delay_hours: 1,
    auto_suspend_delay_credit_enabled: false,
    auto_suspend_delay_credit: 1.0,
    auto_suspend_notification_template: 'account.suspended',
    suspend_instead_of_terminate: false,
    auto_terminate: false,
    auto_terminate_delay_hours: 1,
    auto_terminate_notification_template: 'account.terminated',
    auto_recreate_terminated_service: false,
    auto_terminate_buckets: false,
    auto_terminate_buckets_delay_hours: 1,
    auto_terminate_gpu: false,
    auto_terminate_gpu_delay_hours: 1,
    auto_terminate_bare_metal: false,
    auto_terminate_bare_metal_delay_hours: 1,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [supportsAutoMergeServiceCycleInvoices, setSupportsAutoMergeServiceCycleInvoices] = useState(false);
  const { viewAlert } = useUi();

  useEffect(() => {
    loadBillingConfiguration();
  }, []);

  const loadBillingConfiguration = async () => {
    try {
      setLoading(true);
      // Получаем ID конфигурации из URL или пропсов
      const configId = 1; // Временно хардкодим, в реальности нужно получать из роута
      const response = await configurationService.getBillingConfigurations(configId);
      
      if (response.data) {
        setSupportsAutoMergeServiceCycleInvoices(
          Object.prototype.hasOwnProperty.call(response.data, 'auto_merge_service_cycle_invoices')
        );
        const normalizedData = normalizeBillingConfiguration(response.data);

        setFormData(prev => ({
          ...prev,
          ...normalizedData
        }));
        setProducts(normalizedData.products || []);
      }
    } catch (error) {
      console.error('Error loading billing configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof BillingFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addAutoOrderService = () => {
    setFormData(prev => ({
      ...prev,
      auto_order_services: [
        ...prev.auto_order_services,
        { product: null, product_cycle: null, product_params: '' }
      ]
    }));
  };

  const removeAutoOrderService = (index: number) => {
    setFormData(prev => ({
      ...prev,
      auto_order_services: prev.auto_order_services.filter((_, serviceIndex) => serviceIndex !== index)
    }));
  };

  const handleAutoOrderServiceChange = (
    index: number,
    field: keyof AutoOrderService,
    value: number | string | null
  ) => {
    setFormData(prev => ({
      ...prev,
      auto_order_services: prev.auto_order_services.map((service, serviceIndex) => {
        if (serviceIndex !== index) {
          return service;
        }

        if (field === 'product') {
          return { ...service, product: value as number | null, product_cycle: null };
        }

        return { ...service, [field]: value };
      })
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.auto_create_order && formData.auto_order_services.length === 0) {
      viewAlert({
        severity: 'error',
        message: 'Add at least one auto order service.',
        show: true
      });
      return;
    }

    const hasIncompleteAutoOrderService = formData.auto_order_services.some(
      service => service.product === null || service.product_cycle === null
    );

    if (formData.auto_create_order && hasIncompleteAutoOrderService) {
      viewAlert({
        severity: 'error',
        message: 'Select a product and cycle for each auto order service.',
        show: true
      });
      return;
    }

    setSaving(true);

    try {
      const configId = 1; // Временно хардкодим
      
      // Очищаем данные перед отправкой
      const cleanData: any = { ...formData };
      
      // Убираем null значения и заменяем их на пустые строки
      Object.keys(cleanData).forEach(key => {
        if (cleanData[key] === null) {
          cleanData[key] = '';
        }
      });

      cleanData.company_info = normalizeStringValue(cleanData.company_info);
      cleanData.sender_name = normalizeStringValue(cleanData.sender_name);
      cleanData.sender_email = normalizeStringValue(cleanData.sender_email);
      cleanData.pdf_logo_file_name = normalizeStringValue(cleanData.pdf_logo_file_name);
      cleanData.auto_order_services = formData.auto_order_services.map(service => ({
        ...service,
        product_params: normalizeStringValue(service.product_params) || null,
      }));
      
      // Убираем лишние поля, которые могут вызывать проблемы
      delete cleanData.products;
      delete cleanData.countries;
      delete cleanData.message_templates;
      delete cleanData.credit_message_templates;
      delete cleanData.billing_message_templates;
      delete cleanData.service_message_templates;
      delete cleanData.auto_order_service;
      delete cleanData.auto_order_service_cycle;
      delete cleanData.auto_order_service_params;
      if (!supportsAutoMergeServiceCycleInvoices) {
        delete cleanData.auto_merge_service_cycle_invoices;
      }
      
      // Добавляем id как в оригинале
      cleanData.id = 1;
      
      console.log('Saving billing configuration:', cleanData);
      console.log('Clean data type:', typeof cleanData);
      console.log('Clean data stringified:', JSON.stringify(cleanData));
      console.log('Clean data keys:', Object.keys(cleanData));
      
      const response = await configurationService.updateBillingConfigurations(configId, cleanData);
      console.log('Billing configuration updated successfully:', response);
      
      // Можно добавить уведомление об успешном сохранении
      viewAlert({
        severity: 'information',
        message: 'Configuration saved successfully!',
        show: true
      });
    } catch (error: any) {
      console.error('Error saving billing configuration:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error headers:', error.response?.headers);
      viewAlert({
        severity: 'error',
        message: 'Error saving configuration. Please try again.',
        show: true
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography variant="body">Loading configuration...</Typography>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <CardHeader>
          <div>
            <Typography variant="h2">Configuration default</Typography>
            <Typography variant="body">477 clients on this configuration</Typography>
          </div>
          <div>
            <Button 
              title="Edit" 
              variant="stroke" 
              size="small" 
              onClick={() => {}} 
            />
            <Button 
              title="Delete" 
              variant="stroke" 
              size="small" 
              onClick={() => {}} 
              disabled={true}
            />
          </div>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit}>
            {/* Billing Section */}
            <ExpansionPanel>
              <ExpansionPanelSummary>
                <Typography variant="h3">Billing</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '1' }}>
                    <CheckBox
                      title="Require credit card and save details on sign-up"
                      value={formData.require_credit_card}
                      onChange={() => handleInputChange('require_credit_card', !formData.require_credit_card)}
                    />
                    
                    <Input
                      title="Charge amount on sign-up"
                      placeholder="Charge amount on sign-up"
                      type="number"
                      value={formData.signup_charge_amount}
                      handleChange={(e: any) => {
                        const value = e.target ? e.target.value : e;
                        const numValue = value === '' ? 0 : parseFloat(value) || 0;
                        handleInputChange('signup_charge_amount', numValue);
                      }}
                      disabled={!formData.require_credit_card}
                    />

                    <div style={{ marginTop: '20px' }}>
                      <Typography variant="body-medium">Sign-up charge amount action</Typography>
                      <RadioButtonGroup
                        title=""
                        buttons={[
                          { title: 'Add amount to client credit', value: 'add_to_client', id: 'add_to_client' },
                          { title: 'Refund amount after credit card validation', value: 'refund', id: 'refund' }
                        ]}
                        selectedButton={formData.signup_charge_amount_action}
                        onChange={(value: string) => handleInputChange('signup_charge_amount_action', value)}
                        disabled={!formData.require_credit_card}
                      />
                    </div>

                    <Input
                      title="Required up to date credit for new services"
                      placeholder="Required up to date credit for new services"
                      type="number"
                      value={formData.credit_required}
                      handleChange={(e: any) => {
                        const value = e.target ? e.target.value : e;
                        const numValue = value === '' ? 0 : parseFloat(value) || 0;
                        handleInputChange('credit_required', numValue);
                      }}
                    />

                    <Input
                      title="Up to date credit limit"
                      placeholder="Up to date credit limit"
                      type="number"
                      value={formData.credit_limit}
                      handleChange={(e: any) => {
                        const value = e.target ? e.target.value : e;
                        const numValue = value === '' ? 0 : parseFloat(value) || 0;
                        handleInputChange('credit_limit', numValue);
                      }}
                    />

                    <Input
                      title="Minimum up to date credit to be left after paying an invoice"
                      placeholder="Minimum up to date credit to be left after paying an invoice"
                      type="number"
                      value={formData.minim_uptodate_credit_for_invoice_payment}
                      handleChange={(e: any) => {
                        const value = e.target ? e.target.value : e;
                        const numValue = value === '' ? 0 : parseFloat(value) || 0;
                        handleInputChange('minim_uptodate_credit_for_invoice_payment', numValue);
                      }}
                    />
                  </div>
                  
                  <div style={{ flex: '1' }}>
                    <HelpText>
                      <h3>Require credit card and save details on sign-up</h3>
                      <p>Users will have to validate a credit card before being able to use services. 
                      Require credit card and save details on sign-up needs a configured gateway that allows credit card saving for future automatic charging.</p>
                      
                      <h3>Charge amount on sign-up</h3>
                      <p>Choose the amount users will have to pay in order to validate their accounts. Taxes will be added if applicable.</p>
                      
                      <h3>Sign-up charge amount action</h3>
                      <p>Choose what to do with the amount new users pay in order to validate their account. 
                      This amount may either go into their client credit or be automatically refunded.</p>
                      
                      <h3>Required up to date credit for new services</h3>
                      <p>Once a client has reached this limit, he will no longer be able to create or upgrade resources.
                      Already created cloud resources will not be suspended based on this threshold.
                      Positive and negative values are allowed.</p>
                      
                      <h3>Up to date credit limit</h3>
                      <p>Based on up to date credit limit you can configure suspension and termination rules.
                      Positive and negative values are allowed.</p>
                      
                      <h3>Minimum up to date credit to be left after paying an invoice</h3>
                      <p>Based on this setting a user is not allowed to pay an invoice from his up to date credit (default currency) 
                      if the remaining up to date credit after invoice payment is lower than this value.</p>
                    </HelpText>
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            {/* Limits for clients with agreements */}
            <ExpansionPanel>
              <ExpansionPanelSummary>
                <Typography variant="h3">Limits for clients with agreements</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '1' }}>
                    <Input
                      title="Required up to date credit when on agreement"
                      placeholder="Required up to date credit when on agreement"
                      type="number"
                      value={formData.credit_required_with_agreement}
                      handleChange={(e: any) => {
                        const value = e.target ? e.target.value : e;
                        const numValue = value === '' ? 0 : parseFloat(value) || 0;
                        handleInputChange('credit_required_with_agreement', numValue);
                      }}
                    />
                    
                    <Input
                      title="Up to date credit limit when on agreement"
                      placeholder="Up to date credit limit when on agreement"
                      type="number"
                      value={formData.credit_limit_with_agreement}
                      handleChange={(e: any) => {
                        const value = e.target ? e.target.value : e;
                        const numValue = value === '' ? 0 : parseFloat(value) || 0;
                        handleInputChange('credit_limit_with_agreement', numValue);
                      }}
                    />
                  </div>
                  
                  <div style={{ flex: '1' }}>
                    <HelpText>
                      <h3>Required up to date credit for new services when on agreement</h3>
                      <p>Once a client with a credit card on file with us has reached this limit, he will no longer be able to create or upgrade resources.
                      Already created cloud resources will not be suspended based on this threshold.
                      Positive and negative values are allowed.</p>
                      
                      <h3>Up to date credit limit when on agreement</h3>
                      <p>Based on up to date credit limit you can configure suspension and termination rules for clients that have a billing agreement.
                      Positive and negative values are allowed.</p>
                    </HelpText>
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            {/* Billing cycles */}
            <ExpansionPanel>
              <ExpansionPanelSummary>
                <Typography variant="h3">Billing cycles</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '1' }}>
                    <CheckBox
                      title="Billing cycle as calendar month"
                      value={formData.billing_cycle_as_calendar_month}
                      onChange={() => handleInputChange('billing_cycle_as_calendar_month', !formData.billing_cycle_as_calendar_month)}
                    />
                    
                    <CheckBox
                      title="Mark invoiced periods as paid when using external billing"
                      value={formData.auto_settle_invoiced_periods_external_billing}
                      onChange={() => handleInputChange('auto_settle_invoiced_periods_external_billing', !formData.auto_settle_invoiced_periods_external_billing)}
                    />
                  </div>
                  
                  <div style={{ flex: '1' }}>
                    <HelpText>
                      <h3>Billing cycle as calendar month</h3>
                      <p>Consider a calendar month as billing cycle.
                      Not checking this option will use the default: each client has his own anniversary date based on the date he signed up.
                      Cycle multiplier will be ignored if this option is active.</p>
                      
                      <h3>Mark invoiced periods as paid when using external billing</h3>
                      <p>Invoiced periods will be marked as paid when appropriate and client credit will be updated. 
                      This setting works when fleio is using an external billing system.</p>
                    </HelpText>
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            {/* General */}
            <ExpansionPanel>
              <ExpansionPanelSummary>
                <Typography variant="h3">General</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '1' }}>
                    <Input
                      title="Company info"
                      placeholder="Company info"
                      isTextArea={true}
                      value={formData.company_info}
                      handleChange={(e: any) => handleInputChange('company_info', normalizeStringValue(e))}
                    />
                    
                    <Input
                      title="Notifications sender name"
                      placeholder="Notifications sender name"
                      value={formData.sender_name}
                      handleChange={(e: any) => handleInputChange('sender_name', normalizeStringValue(e))}
                    />
                    
                    <Input
                      title="Notifications sender email"
                      placeholder="Notifications sender email"
                      value={formData.sender_email}
                      handleChange={(e: any) => handleInputChange('sender_email', normalizeStringValue(e))}
                    />
                    
                    <CheckBox
                      title="Use custom logo for pdf invoices"
                      value={formData.use_custom_logo_for_pdf_invoices}
                      onChange={() => handleInputChange('use_custom_logo_for_pdf_invoices', !formData.use_custom_logo_for_pdf_invoices)}
                    />
                    
                    {formData.use_custom_logo_for_pdf_invoices && (
                      <Typography variant="body">{`Current logo file name: ${formData.pdf_logo_file_name}`}</Typography>
                    )}
                  </div>
                  
                  <div style={{ flex: '1' }}>
                    <HelpText>
                      <h3>Company info</h3>
                      <p>Company information like name and other billing details.
                      This information is shown on invoices.</p>
                      
                      <h3>Notifications sender name</h3>
                      <p>The name to send the notifications from.
                      Leaving this blank will use just the Notifications sender email.</p>
                      
                      <h3>Notifications sender email</h3>
                      <p>The e-mail address the notifications are sent from.
                      Leaving this blank will use the Fleio DEFAULT_FROM_EMAIL setting.</p>
                      
                      <h3>Use custom logo for pdf invoices</h3>
                      <p>Checking this will insert a custom logo into pdf invoices. You can upload a logo below.</p>
                    </HelpText>
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            {/* MaxMind fraud check */}
            <ExpansionPanel>
              <ExpansionPanelSummary>
                <Typography variant="h3">MaxMind fraud check</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '1' }}>
                    <CheckBox
                      title="Enable MaxMind fraud check"
                      value={formData.fraud_check}
                      onChange={() => handleInputChange('fraud_check', !formData.fraud_check)}
                    />
                    
                    <CheckBox
                      title="Enable insights results"
                      value={formData.enable_maxmind_insights}
                      onChange={() => handleInputChange('enable_maxmind_insights', !formData.enable_maxmind_insights)}
                      disabled={!formData.fraud_check}
                    />
                    
                    <CheckBox
                      title="Skip fraud checks for active clients"
                      value={formData.skip_fraud_check_for_active_clients}
                      onChange={() => handleInputChange('skip_fraud_check_for_active_clients', !formData.skip_fraud_check_for_active_clients)}
                      disabled={!formData.fraud_check}
                    />
                    
                    <CheckBox
                      title="Automatically mark new orders as fraud for clients with fraud order"
                      value={formData.automatically_mark_new_orders_as_fraud_for_clients_with_fraud_order}
                      onChange={() => handleInputChange('automatically_mark_new_orders_as_fraud_for_clients_with_fraud_order', !formData.automatically_mark_new_orders_as_fraud_for_clients_with_fraud_order)}
                      disabled={!formData.fraud_check}
                    />
                    
                    <Input
                      title="Fraud score"
                      placeholder="Fraud score"
                      type="number"
                      value={formData.maxmind_fraud_score}
                      handleChange={(e: any) => {
                        const value = e.target ? e.target.value : e;
                        const numValue = value === '' ? 0 : parseFloat(value) || 0;
                        handleInputChange('maxmind_fraud_score', numValue);
                      }}
                      disabled={!formData.fraud_check}
                    />
                  </div>
                  
                  <div style={{ flex: '1' }}>
                    <HelpText>
                      <h3>MaxMind fraud check</h3>
                      <p>Enable or disable MaxMind fraud checking on new orders.
                      To completely enable this module, make sure you have the MAXMIND_CLIENTID and MAXMIND_LICENSE options set in settings.py
                      The score for manual review can be set or when an order is automatically marked as fraud.</p>
                    </HelpText>
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            {/* New client automation */}
            <ExpansionPanel>
              <ExpansionPanelSummary>
                <Typography variant="h3">New client automation</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '1' }}>
                    <CheckBox
                      title="Automatically create order when client is created"
                      value={formData.auto_create_order}
                      onChange={() => handleInputChange('auto_create_order', !formData.auto_create_order)}
                    />

                    <AutomationServicesHeader>
                      <Typography variant="body-medium">Auto order services</Typography>
                      <Button
                        title="Add service"
                        variant="stroke"
                        size="small"
                        icon={icons.PlusDefault}
                        onClick={addAutoOrderService}
                        disabled={!formData.auto_create_order}
                      />
                    </AutomationServicesHeader>

                    {formData.auto_order_services.map((service, index) => {
                      const productCycles = products.find(product => product.id === service.product)?.cycles || [];

                      return (
                        <AutomationServiceCard key={index}>
                          <AutomationServiceCardHeader>
                            <Typography variant="h3">Service #{index + 1}</Typography>
                            <Button
                              title="Delete"
                              variant="stroke"
                              size="small"
                              icon={icons.DeleteIcon}
                              onClick={() => removeAutoOrderService(index)}
                              disabled={!formData.auto_create_order}
                            />
                          </AutomationServiceCardHeader>
                          <Select
                            title="Auto order service"
                            data={products.map(product => ({ id: product.id, title: product.name }))}
                            selectedValue={service.product}
                            onChange={(value: any) => handleAutoOrderServiceChange(index, 'product', value)}
                            disabled={!formData.auto_create_order}
                          />
                          <Select
                            title="Auto order service cycle"
                            data={productCycles.map((cycle: any) => ({ id: cycle.id, title: cycle.display_name }))}
                            selectedValue={service.product_cycle}
                            onChange={(value: any) => handleAutoOrderServiceChange(index, 'product_cycle', value)}
                            disabled={!formData.auto_create_order || service.product === null}
                          />
                          <Input
                            title="Auto order parameters as JSON"
                            placeholder="Auto order parameters as JSON"
                            isTextArea={true}
                            value={service.product_params}
                            handleChange={(e: any) => {
                              const value = e.target ? e.target.value : e;
                              handleAutoOrderServiceChange(index, 'product_params', value);
                            }}
                            readOnly={!formData.auto_create_order}
                          />
                        </AutomationServiceCard>
                      );
                    })}
                    
                    <Input
                      title="Client initial credit (default currency)"
                      placeholder="Client initial credit (default currency)"
                      type="number"
                      value={formData.client_initial_credit}
                      handleChange={(e: any) => {
                        const value = e.target ? e.target.value : e;
                        const numValue = value === '' ? 0 : parseFloat(value) || 0;
                        handleInputChange('client_initial_credit', numValue);
                      }}
                    />
                  </div>
                  
                  <div style={{ flex: '1' }}>
                    <HelpText>
                      <h3>New client automation</h3>
                      <p>If "Automatically create order when client is created" is checked, an order is placed automatically when end-user creates a client (on sign-up or when adding additional clients).
                      On create client staff form, a checkbox is available to choose if you want to place the order.
                      The "Auto order service cycle" is used with the default currency. If cycle with the default currency does not exist, the order creation fails, and an error is logged.
                      The amount specified in "Client initial credit" is added to the client's credit balance. If client has another currency than default one, the amount is converted.
                      Additional parameters can be set as JSON to be sent to the service module on creation.</p>
                    </HelpText>
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            {/* Invoicing */}
            <ExpansionPanel>
              <ExpansionPanelSummary>
                <Typography variant="h3">Invoicing</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '1' }}>
                    <CheckBox
                      title="Generate invoices"
                      value={formData.generate_invoices}
                      onChange={() => handleInputChange('generate_invoices', !formData.generate_invoices)}
                    />
                    
                    <CheckBox
                      title="Generate invoices for service billing cycles ended after:"
                      value={formData.generate_invoices_after}
                      onChange={() => handleInputChange('generate_invoices_after', !formData.generate_invoices_after)}
                    />
                    
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                      <Input
                        title="Date"
                        placeholder="Date"
                        type="date"
                        value={formData.generate_invoices_after_dt}
                        handleChange={(value: any) => handleInputChange('generate_invoices_after_dt', value)}
                        disabled={!formData.generate_invoices_after}
                      />
                      <Input
                        title="Time (HH:mm:ss)"
                        placeholder="HH:mm:ss"
                        value={formData.generate_invoices_after_time}
                        handleChange={(value: any) => handleInputChange('generate_invoices_after_time', value)}
                        disabled={!formData.generate_invoices_after}
                      />
                    </div>
                    
                    <CheckBox
                      title="Do not invoice services with zero price"
                      value={formData.do_not_invoice_services_with_zero_price}
                      onChange={() => handleInputChange('do_not_invoice_services_with_zero_price', !formData.do_not_invoice_services_with_zero_price)}
                    />

                    {supportsAutoMergeServiceCycleInvoices ? (
                      <CheckBox
                        title="Automatically merge service cycle invoices"
                        value={formData.auto_merge_service_cycle_invoices}
                        onChange={() => handleInputChange('auto_merge_service_cycle_invoices', !formData.auto_merge_service_cycle_invoices)}
                      />
                    ) : null}

                    <CheckBox
                      title="Notify on unpaid invoices"
                      value={formData.send_notifications_for_unpaid_invoices}
                      onChange={() => handleInputChange('send_notifications_for_unpaid_invoices', !formData.send_notifications_for_unpaid_invoices)}
                    />
                    
                    <CheckBox
                      title="Create TODO on invoice payment"
                      value={formData.create_todo_on_invoice_payment}
                      onChange={() => handleInputChange('create_todo_on_invoice_payment', !formData.create_todo_on_invoice_payment)}
                    />
                    
                    <CheckBox
                      title="Enable automatic settlements"
                      value={formData.auto_settle_usage}
                      onChange={() => handleInputChange('auto_settle_usage', !formData.auto_settle_usage)}
                    />
                    
                    <CheckBox
                      title="Auto pay invoice only when enough credit"
                      value={formData.auto_pay_invoice_only_when_enough_credit}
                      onChange={() => handleInputChange('auto_pay_invoice_only_when_enough_credit', !formData.auto_pay_invoice_only_when_enough_credit)}
                    />

                    <Input
                      title="Minimum add credit value"
                      placeholder="Minimum add credit value"
                      type="number"
                      value={formData.add_credit_min_value}
                      handleChange={(e: any) => {
                        const value = e.target ? e.target.value : e;
                        const numValue = value === '' ? 0 : Math.max(0, parseFloat(value) || 0);
                        handleInputChange('add_credit_min_value', numValue);
                      }}
                    />
                  </div>
                  
                  <div style={{ flex: '1' }}>
                    <HelpText>
                      <h3>Invoicing</h3>
                      <p>Configure various invoicing options including automatic invoice generation, payment reminders, and settlement settings.</p>
                    </HelpText>
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            {/* Invoice notifications */}
            <ExpansionPanel>
              <ExpansionPanelSummary>
                <Typography variant="h3">Invoice notifications</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '1' }}>
                    <CheckBox
                      title="Enable invoice notification"
                      value={formData.invoice_notifications_enabled || false}
                      onChange={() => handleInputChange('invoice_notifications_enabled', !(formData.invoice_notifications_enabled || false))}
                    />
                    
                    <Typography variant="body-medium">Invoice payment reminder notifications</Typography>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                      <Input
                        title="Hours remaining"
                        placeholder="Hours remaining"
                        type="number"
                        value={formData.first_invoice_payment_reminder_remaining_hours || 168}
                        handleChange={(e: any) => {
                          const value = e.target ? e.target.value : e;
                          const numValue = value === '' ? 0 : parseInt(value) || 0;
                          handleInputChange('first_invoice_payment_reminder_remaining_hours', numValue);
                        }}
                      />
                      <Select
                        title="Message template"
                        data={[
                          { id: 'billing.invoice.payment.reminder', title: 'billing.invoice.payment.reminder', label: 'billing.invoice.payment.reminder' },
                          { id: 'blank', title: 'Blank notification', label: 'Blank notification' }
                        ]}
                        selectedValue={formData.first_invoice_payment_reminder_notification_template || 'billing.invoice.payment.reminder'}
                        onChange={(value: any) => {
                          const templateValue = typeof value === 'object' ? value.id : value;
                          handleInputChange('first_invoice_payment_reminder_notification_template', templateValue);
                        }}
                      />
                    </div>
                    
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                      <Input
                        title="Hours remaining"
                        placeholder="Hours remaining"
                        type="number"
                        value={formData.second_invoice_payment_reminder_remaining_hours || 48}
                        handleChange={(e: any) => {
                          const value = e.target ? e.target.value : e;
                          const numValue = value === '' ? 0 : parseInt(value) || 0;
                          handleInputChange('second_invoice_payment_reminder_remaining_hours', numValue);
                        }}
                      />
                      <Select
                        title="Message template"
                        data={[
                          { id: 'billing.invoice.payment.reminder', title: 'billing.invoice.payment.reminder', label: 'billing.invoice.payment.reminder' },
                          { id: 'blank', title: 'Blank notification', label: 'Blank notification' }
                        ]}
                        selectedValue={formData.second_invoice_payment_reminder_notification_template || 'billing.invoice.payment.reminder'}
                        onChange={(value: any) => {
                          const templateValue = typeof value === 'object' ? value.id : value;
                          handleInputChange('second_invoice_payment_reminder_notification_template', templateValue);
                        }}
                      />
                    </div>
                    
                    <Typography variant="body-medium">Invoice overdue notifications</Typography>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                      <Input
                        title="Hours since overdue"
                        placeholder="Hours since overdue"
                        type="number"
                        value={formData.first_invoice_payment_overdue_hours || 24}
                        handleChange={(e: any) => {
                          const value = e.target ? e.target.value : e;
                          const numValue = value === '' ? 0 : parseInt(value) || 0;
                          handleInputChange('first_invoice_payment_overdue_hours', numValue);
                        }}
                      />
                      <Select
                        title="Message template"
                        data={[
                          { id: 'billing.invoice.payment.overdue', title: 'billing.invoice.payment.overdue', label: 'billing.invoice.payment.overdue' },
                          { id: 'blank', title: 'Blank notification', label: 'Blank notification' }
                        ]}
                        selectedValue={formData.first_invoice_payment_overdue_notification_template || 'billing.invoice.payment.overdue'}
                        onChange={(value: any) => {
                          const templateValue = typeof value === 'object' ? value.id : value;
                          handleInputChange('first_invoice_payment_overdue_notification_template', templateValue);
                        }}
                      />
                    </div>
                    
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                      <Input
                        title="Hours since overdue"
                        placeholder="Hours since overdue"
                        type="number"
                        value={formData.second_invoice_payment_overdue_hours || 72}
                        handleChange={(e: any) => {
                          const value = e.target ? e.target.value : e;
                          const numValue = value === '' ? 0 : parseInt(value) || 0;
                          handleInputChange('second_invoice_payment_overdue_hours', numValue);
                        }}
                      />
                      <Select
                        title="Message template"
                        data={[
                          { id: 'billing.invoice.payment.overdue', title: 'billing.invoice.payment.overdue', label: 'billing.invoice.payment.overdue' },
                          { id: 'blank', title: 'Blank notification', label: 'Blank notification' }
                        ]}
                        selectedValue={formData.second_invoice_payment_overdue_notification_template || 'billing.invoice.payment.overdue'}
                        onChange={(value: any) => {
                          const templateValue = typeof value === 'object' ? value.id : value;
                          handleInputChange('second_invoice_payment_overdue_notification_template', templateValue);
                        }}
                      />
                    </div>
                    
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                      <Input
                        title="Hours since overdue"
                        placeholder="Hours since overdue"
                        type="number"
                        value={formData.third_invoice_payment_overdue_hours || 120}
                        handleChange={(e: any) => {
                          const value = e.target ? e.target.value : e;
                          const numValue = value === '' ? 0 : parseInt(value) || 0;
                          handleInputChange('third_invoice_payment_overdue_hours', numValue);
                        }}
                      />
                      <Select
                        title="Message template"
                        data={[
                          { id: 'billing.invoice.payment.overdue', title: 'billing.invoice.payment.overdue', label: 'billing.invoice.payment.overdue' },
                          { id: 'blank', title: 'Blank notification', label: 'Blank notification' }
                        ]}
                        selectedValue={formData.third_invoice_payment_overdue_notification_template || 'billing.invoice.payment.overdue'}
                        onChange={(value: any) => {
                          const templateValue = typeof value === 'object' ? value.id : value;
                          handleInputChange('third_invoice_payment_overdue_notification_template', templateValue);
                        }}
                      />
                    </div>
                    
                    <Select
                      title="Invoice payment confirmation message template"
                      data={[
                        { id: 'billing.invoice.payment.confirmation', title: 'billing.invoice.payment.confirmation', label: 'billing.invoice.payment.confirmation' },
                        { id: 'blank', title: 'Blank notification', label: 'Blank notification' }
                      ]}
                      selectedValue={formData.invoice_payment_confirmation_notification_template || 'billing.invoice.payment.confirmation'}
                      onChange={(value: any) => {
                        const templateValue = typeof value === 'object' ? value.id : value;
                        handleInputChange('invoice_payment_confirmation_notification_template', templateValue);
                      }}
                    />
                    
                    <Select
                      title="Invoice refund confirmation message template"
                      data={[
                        { id: 'billing.invoice.refund.confirmation', title: 'billing.invoice.refund.confirmation', label: 'billing.invoice.refund.confirmation' },
                        { id: 'blank', title: 'Blank notification', label: 'Blank notification' }
                      ]}
                      selectedValue={formData.invoice_refund_confirmation_notification_template || 'billing.invoice.refund.confirmation'}
                      onChange={(value: any) => {
                        const templateValue = typeof value === 'object' ? value.id : value;
                        handleInputChange('invoice_refund_confirmation_notification_template', templateValue);
                      }}
                    />
                  </div>
                  
                  <div style={{ flex: '1' }}>
                    <HelpText>
                      <h3>Enable invoice notification</h3>
                      <p>Send invoice related notifications (payment reminders, payment overdue reminders, payment confirmation and refund confirmation)</p>
                      
                      <h3>Invoice payment reminder notifications</h3>
                      <p>Select how many hours until the invoice is due and a message template to send. Selecting the blank notification will disable it.</p>
                      
                      <h3>Invoice payment overdue notifications</h3>
                      <p>Select how many hours since the invoice was due and a message template to send. Selecting the blank notification will disable it.</p>
                      
                      <h3>Invoice payment confirmation message template</h3>
                      <p>Select a message template to send. Selecting the blank notification will disable it.</p>
                      
                      <h3>Invoice refund confirmation message template</h3>
                      <p>Select a message template to send. Selecting the blank notification will disable it.</p>
                    </HelpText>
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            {/* Service notifications */}
            <ExpansionPanel>
              <ExpansionPanelSummary>
                <Typography variant="h3">Service notifications</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '1' }}>
                    <CheckBox
                      title="Enable service notifications"
                      value={formData.service_notifications_enabled || false}
                      onChange={() => handleInputChange('service_notifications_enabled', !(formData.service_notifications_enabled || false))}
                    />
                    
                    <Select
                      title="Service suspended message template"
                      data={[
                        { id: 'service.suspended', title: 'service.suspended', label: 'service.suspended' },
                        { id: 'blank', title: 'Blank notification', label: 'Blank notification' }
                      ]}
                      selectedValue={formData.service_suspended_notification_template || 'service.suspended'}
                      onChange={(value: any) => {
                        const templateValue = typeof value === 'object' ? value.id : value;
                        handleInputChange('service_suspended_notification_template', templateValue);
                      }}
                      disabled={!formData.service_notifications_enabled}
                    />
                    
                    <Select
                      title="Service resumed message template"
                      data={[
                        { id: 'service.resumed', title: 'service.resumed', label: 'service.resumed' },
                        { id: 'blank', title: 'Blank notification', label: 'Blank notification' }
                      ]}
                      selectedValue={formData.service_resumed_notification_template || 'service.resumed'}
                      onChange={(value: any) => {
                        const templateValue = typeof value === 'object' ? value.id : value;
                        handleInputChange('service_resumed_notification_template', templateValue);
                      }}
                      disabled={!formData.service_notifications_enabled}
                    />
                  </div>
                  
                  <div style={{ flex: '1' }}>
                    <HelpText>
                      <h3>Enable service notification</h3>
                      <p>Send notifications when a client's services are suspended or resumed</p>
                      
                      <h3>Service suspended notification</h3>
                      <p>Notification to send when a service is suspended</p>
                      
                      <h3>Service resumed notification</h3>
                      <p>Notification to send when a service is resumed</p>
                    </HelpText>
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            {/* Suspension */}
            <ExpansionPanel>
              <ExpansionPanelSummary>
                <Typography variant="h3">Suspension</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '1' }}>
                    <CheckBox
                      title="Enable automatic suspension"
                      value={formData.auto_suspend || false}
                      onChange={() => handleInputChange('auto_suspend', !(formData.auto_suspend || false))}
                    />
                    
                    <CheckBox
                      title="Enable delay by hours"
                      value={formData.auto_suspend_delay_hours_enabled || false}
                      onChange={() => handleInputChange('auto_suspend_delay_hours_enabled', !(formData.auto_suspend_delay_hours_enabled || false))}
                    />
                    
                    <Input
                      title="Delay(hours)"
                      placeholder="Delay(hours)"
                      type="number"
                      value={formData.auto_suspend_delay_hours || 1}
                      handleChange={(e: any) => {
                        const value = e.target ? e.target.value : e;
                        const numValue = value === '' ? 0 : parseInt(value) || 0;
                        handleInputChange('auto_suspend_delay_hours', numValue);
                      }}
                    />
                    
                    <CheckBox
                      title="Enable delay until credit is used"
                      value={formData.auto_suspend_delay_credit_enabled || false}
                      onChange={() => handleInputChange('auto_suspend_delay_credit_enabled', !(formData.auto_suspend_delay_credit_enabled || false))}
                    />
                    
                    <Input
                      title="Delay(credit)"
                      placeholder="Delay(credit)"
                      type="number"
                      value={formData.auto_suspend_delay_credit || 1.0}
                      handleChange={(e: any) => {
                        const value = e.target ? e.target.value : e;
                        const numValue = value === '' ? 0 : parseFloat(value) || 0;
                        handleInputChange('auto_suspend_delay_credit', numValue);
                      }}
                    />
                    
                    <Select
                      title="Message template"
                      data={[
                        { id: 'account.suspended', title: 'account.suspended', label: 'account.suspended' },
                        { id: 'blank', title: 'Blank notification', label: 'Blank notification' }
                      ]}
                      selectedValue={formData.auto_suspend_notification_template || 'account.suspended'}
                      onChange={(value: any) => {
                        const templateValue = typeof value === 'object' ? value.id : value;
                        handleInputChange('auto_suspend_notification_template', templateValue);
                      }}
                    />
                  </div>
                  
                  <div style={{ flex: '1' }}>
                    <HelpText>
                      <h3>Enable automatic suspension</h3>
                      <p>Automatically suspend a client when he reaches the up to date credit limit.</p>
                      <p>The actual suspension can be postponed by a few hours when <strong>Delay by hours</strong> is checked or until client is under the up to date credit limit by a specified amount when <strong>Delay until credit is used</strong> is checked.</p>
                      <p>Selecting a <strong>message template</strong> will send a notification to the client on suspension.</p>
                    </HelpText>
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            {/* Termination */}
            <ExpansionPanel>
              <ExpansionPanelSummary>
                <Typography variant="h3">Termination</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: '1', display: 'flex', flexDirection:'column', gap: '14px'}}>
                    <CheckBox
                      title="Suspend the client instead of termination"
                      value={formData.suspend_instead_of_terminate || false}
                      onChange={() => handleInputChange('suspend_instead_of_terminate', !(formData.suspend_instead_of_terminate || false))}
                    />
                    
                    <Typography variant="small" customStyles={{ color: '#d32f2f', marginBottom: '10px' }}>
                      Un-checking suspend instead of terminate allows clients and service termination and may cause accidental data loss. However, you can remove permissions for service/client deletion on authorization page and this will prevent accidental data loss.
                    </Typography>
                    
                    <CheckBox
                      title="Enable automatic termination"
                      value={formData.auto_terminate || false}
                      onChange={() => handleInputChange('auto_terminate', !(formData.auto_terminate || false))}
                    />
                    
                    <CheckBox
                      title="Auto recreate terminated service"
                      value={formData.auto_recreate_terminated_service || false}
                      onChange={() => handleInputChange('auto_recreate_terminated_service', !(formData.auto_recreate_terminated_service || false))}
                    />
                    
                    <Input
                      title="Delay(hours)"
                      placeholder="Delay(hours)"
                      type="number"
                      value={formData.auto_terminate_delay_hours || 1}
                      handleChange={(e: any) => {
                        const value = e.target ? e.target.value : e;
                        const numValue = value === '' ? 0 : parseInt(value) || 0;
                        handleInputChange('auto_terminate_delay_hours', numValue);
                      }}
                    />

                    <CheckBox
                      title="Enable buckets termination"
                      value={formData.auto_terminate_buckets || false}
                      onChange={() => handleInputChange('auto_terminate_buckets', !(formData.auto_terminate_buckets || false))}
                    />

                    <Input
                      title="Buckets termination delay(hours)"
                      placeholder="Buckets termination delay(hours)"
                      type="number"
                      value={formData.auto_terminate_buckets_delay_hours || 1}
                      handleChange={(e: any) => {
                        const value = e.target ? e.target.value : e;
                        const numValue = value === '' ? 0 : parseInt(value) || 0;
                        handleInputChange('auto_terminate_buckets_delay_hours', numValue);
                      }}
                    />

                    <CheckBox
                      title="Enable GPU termination"
                      value={formData.auto_terminate_gpu || false}
                      onChange={() => handleInputChange('auto_terminate_gpu', !(formData.auto_terminate_gpu || false))}
                    />

                    <Input
                      title="GPU termination delay(hours)"
                      placeholder="GPU termination delay(hours)"
                      type="number"
                      value={formData.auto_terminate_gpu_delay_hours || 1}
                      handleChange={(e: any) => {
                        const value = e.target ? e.target.value : e;
                        const numValue = value === '' ? 0 : parseInt(value) || 0;
                        handleInputChange('auto_terminate_gpu_delay_hours', numValue);
                      }}
                    />

                    <CheckBox
                      title="Enable bare metal termination"
                      value={formData.auto_terminate_bare_metal || false}
                      onChange={() => handleInputChange('auto_terminate_bare_metal', !(formData.auto_terminate_bare_metal || false))}
                    />

                    <Input
                      title="Bare metal termination delay(hours)"
                      placeholder="Bare metal termination delay(hours)"
                      type="number"
                      value={formData.auto_terminate_bare_metal_delay_hours || 1}
                      handleChange={(e: any) => {
                        const value = e.target ? e.target.value : e;
                        const numValue = value === '' ? 0 : parseInt(value) || 0;
                        handleInputChange('auto_terminate_bare_metal_delay_hours', numValue);
                      }}
                    />
                    
                    <Select
                      title="Message template"
                      data={[
                        { id: 'account.terminated', title: 'account.terminated', label: 'account.terminated' },
                        { id: 'blank', title: 'Blank notification', label: 'Blank notification' }
                      ]}
                      selectedValue={formData.auto_terminate_notification_template || 'account.terminated'}
                      onChange={(value: any) => {
                        const templateValue = typeof value === 'object' ? value.id : value;
                        handleInputChange('auto_terminate_notification_template', templateValue);
                      }}
                    />
                  </div>
                  
                  <div style={{ flex: '1' }}>
                    <HelpText>
                      <h3>Suspend the client instead of termination</h3>
                      <p>This overrides the default termination action and suspends the client and his resources instead.</p>
                      
                      <h3>Enable automatic termination</h3>
                      <p>Automatically terminates a service after certain time (defined in delay field) passed since service suspension.</p>
                      <p>A <strong>Delay</strong> for service termination can be set (in hours) to postpone the actual termination after service was suspended.</p>
                      <p>The client can be notified about the termination if a <strong>message template</strong> is selected.</p>
                      
                      <h3>Auto recreate terminated service</h3>
                      <p>Automatically recreate a service after it has been terminated. This option allows for automatic service restoration.</p>
                    </HelpText>
                  </div>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>

            <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <Button 
                title="Cancel" 
                variant="stroke" 
                size="small" 
                onClick={() => {}} 
              />
              <Button 
                title="Save Configuration" 
                variant="primary" 
                size="small" 
                loading={saving}
              />
            </div>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
}

export default BillingPage;
